import React, { useState, useEffect } from 'react';
import { Link as RouterLink, Outlet, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  ListItemButton,
  Avatar,
  //Badge,
  alpha,
  styled,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import CampaignIcon from '@mui/icons-material/Campaign';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
//import NotificationsIcon from '@mui/icons-material/Notifications';
//import SettingsIcon from '@mui/icons-material/Settings';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import TaskIcon from '@mui/icons-material/Task';
import HistoryIcon from '@mui/icons-material/History';
import SecurityIcon from '@mui/icons-material/Security';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LinkIcon from '@mui/icons-material/Link';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { logout } from '../../store/slices/authSlice';
import type { RootState, AppDispatch } from '../../store/store';
import { colors } from '../../styles/theme';
import ChangelogDialog from './ChangelogDialog';
import { fetchVersion, fetchChangelogs, type ChangelogEntry } from '../../services/versionService';
import { isVersionNewer } from '../../utils/semver';
import GlobalAlertTimer from '../GlobalAlertTimer';
import { getGlobalIntervalManager } from '../../utils/useAlertsAutoRefresh';

const drawerWidth = 260;

// Logo component
const Logo = styled('div')({
  display: 'flex',
  alignItems: 'center',
  padding: '0 16px',
  '& img': {
    height: '32px',
    marginRight: '12px',
  },
  '& .logo-text': {
    fontWeight: 700,
    fontSize: '1.5rem',
    background: `linear-gradient(45deg, ${colors.primaryRed} 30%, ${colors.lightRed} 90%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    letterSpacing: '0.5px',
  },
});

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

// Base navigation items available to all authenticated users
const baseNavItems: NavItem[] = [
  { text: 'Dashboard', icon: <HomeIcon />, path: '/' },
  { text: 'Characters', icon: <PeopleIcon />, path: '/characters' },
  { text: 'Item Inventory', icon: <InventoryIcon />, path: '/items' },
  { text: 'Alerts', icon: <CampaignIcon />, path: '/alerts' },
  { text: 'Market Analysis', icon: <AnalyticsIcon />, path: '/market' },
];

// Admin-only navigation item
const adminNavItems: NavItem[] = [
  {
    text: 'Owners',
    icon: <PersonAddIcon />,
    path: '/owners',
  },
  {
    text: 'Tasks',
    icon: <TaskIcon />,
    path: '/tasks',
  },
  {
    text: 'Cleanup',
    icon: <DeleteSweepIcon />,
    path: '/cleanup',
  },
];

const ExileGuardDashboardLayout: React.FC = () => {
  const theme = useTheme();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);

  // Changelog state
  const [newVersionDialogOpen, setNewVersionDialogOpen] = useState(false);
  const [fullChangelogOpen, setFullChangelogOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [allChangelogs, setAllChangelogs] = useState<ChangelogEntry[]>([]);
  const [newChangelogs, setNewChangelogs] = useState<ChangelogEntry[]>([]);

  // Construct navigation items based on user role
  const navItems = [...baseNavItems];
  if (user && user.roles && user.roles.includes('admin')) {
    navItems.push(...adminNavItems);
  }
  // Add ExileGuard nav item for users with the feature
  if (user && user.features && user.features.includes('ExileGuard')) {
    navItems.push({
      text: 'ExileGuard',
      icon: <SecurityIcon />,
      path: '/exileguard',
    });
  }

  // Add Timetables nav item for users with the feature
  if (user && user.features && user.features.includes('Timetables')) {
    navItems.push({
      text: 'Timetables',
      icon: <ScheduleIcon />,
      path: '/timetables',
    });
  }

  // Add SOP Mappings nav item for users with the AlertSopLinks feature
  if (user && user.features && user.features.includes('AlertSopLinks')) {
    navItems.push({
      text: 'SOP Mappings',
      icon: <LinkIcon />,
      path: '/alert-sop-mappings',
    });
  }

  // Add RMT nav item for users with the feature
  if (user && user.features && user.features.includes('RMT')) {
    navItems.push({
      text: 'RMT',
      icon: <AttachMoneyIcon />,
      path: '/rmt',
    });
  }

  // Add Flips nav item for users with the feature
  if (user && user.features && user.features.includes('Flips')) {
    navItems.push({
      text: 'Flips',
      icon: <SwapHorizIcon />,
      path: '/flips',
    });
  }

  // Load version and changelog data
  useEffect(() => {
    const loadVersionData = async () => {
      try {
        const [version, changelogs] = await Promise.all([fetchVersion(), fetchChangelogs()]);

        setCurrentVersion(version);
        setAllChangelogs(changelogs);

        // Check if we need to show new version dialog
        const lastSeenVersion = localStorage.getItem('dashboardLastSeenVersion');

        if (!lastSeenVersion || isVersionNewer(version, lastSeenVersion)) {
          // Filter changelogs to show only new ones
          const newEntries = lastSeenVersion
            ? changelogs.filter(entry => isVersionNewer(entry.version, lastSeenVersion))
            : changelogs.filter(entry => entry.version === version);

          if (newEntries.length > 0) {
            setNewChangelogs(newEntries);
            setNewVersionDialogOpen(true);
          }
        }
      } catch (error) {
        console.error('Failed to load version data:', error);
      }
    };

    loadVersionData();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleCloseNewVersionDialog = () => {
    localStorage.setItem('dashboardLastSeenVersion', currentVersion);
    setNewVersionDialogOpen(false);
  };

  const handleOpenFullChangelog = () => {
    setFullChangelogOpen(true);
  };

  const handleManualAlertRefresh = () => {
    const globalManager = getGlobalIntervalManager();
    globalManager.triggerRefresh();
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px !important', // Override MUI default
        }}
      >
        <Logo>
          {/* Placeholder for logo - you can replace with an actual image */}
          <span className='logo-text'>ExileGuard</span>
        </Logo>
        <IconButton onClick={handleDrawerToggle} sx={{ display: { sm: 'none' } }}>
          <ChevronLeftIcon />
        </IconButton>
      </Toolbar>
      <Divider />

      {/* User profile section */}
      {user && (
        <Box sx={{ padding: '16px', textAlign: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              margin: '0 auto 8px auto',
              backgroundColor: colors.primaryRed,
              border: `2px solid ${colors.lightRed}`,
            }}
          >
            {user.username ? user.username[0].toUpperCase() : 'U'}
          </Avatar>
          <Typography variant='subtitle1' sx={{ fontWeight: '500' }}>
            {user.username || user.email}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {user.roles && user.roles.includes('admin') ? 'Administrator' : 'User'}
          </Typography>
        </Box>
      )}
      <Divider />

      {/* Navigation links */}
      <List sx={{ flexGrow: 1, px: 1 }}>
        {navItems.map(item => (
          <ListItem
            key={item.text}
            disablePadding
            component={RouterLink}
            to={item.path}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              mb: 0.5,
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <ListItemButton sx={{ borderRadius: '4px' }}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* Changelog and Logout section */}
      <Divider />
      <List sx={{ padding: '8px' }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleOpenFullChangelog}
            sx={{
              borderRadius: '4px',
              mb: 0.5,
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}>
              <HistoryIcon />
            </ListItemIcon>
            <ListItemText primary='Changelog' />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
              },
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.error.main }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary='Logout' />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position='fixed'
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          backdropFilter: 'blur(8px)',
          backgroundColor: alpha(colors.primaryBg, 0.8),
        }}
      >
        <Toolbar>
          <IconButton
            color='inherit'
            aria-label='open drawer'
            edge='start'
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          {/* Alert Timer */}
          <GlobalAlertTimer onManualRefresh={handleManualAlertRefresh} />

          {/* Version Display */}
          {currentVersion && (
            <Typography variant='caption' sx={{ mr: 2, ml: 2, opacity: 0.7 }}>
              v{currentVersion}
            </Typography>
          )}

          {/* App Bar Icons */}
          {/* <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton color="inherit" size="large" aria-label="show notifications">
              <Badge badgeContent={4} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <IconButton color="inherit" size="large" sx={{ ml: 1 }}>
              <SettingsIcon />
            </IconButton>
          </Box> */}
        </Toolbar>
      </AppBar>

      <Box
        component='nav'
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
        }}
        aria-label='mailbox folders'
      >
        {/* Mobile drawer */}
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant='permanent'
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: `1px solid ${colors.dividerGray}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: colors.primaryBg,
          minHeight: '100vh',
        }}
      >
        <Toolbar /> {/* Spacer for the AppBar */}
        <Outlet />
      </Box>

      {/* Changelog Dialogs */}
      <ChangelogDialog
        open={newVersionDialogOpen}
        onClose={handleCloseNewVersionDialog}
        entries={newChangelogs}
        isNewVersionDialog={true}
      />

      <ChangelogDialog
        open={fullChangelogOpen}
        onClose={() => setFullChangelogOpen(false)}
        entries={allChangelogs}
        isNewVersionDialog={false}
      />
    </Box>
  );
};

export default ExileGuardDashboardLayout;
